"use client"

import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import Link from "next/link"
import { ArrowRight, BadgeCheck, Ban, Droplet, Gauge, Ruler } from "lucide-react"
import { Card } from "@/components/ui/card"
import { RelatedTools, ResultEmailCaptureCard } from "@/components/tools/shared"
import { UnifiedShareButton } from "@/components/tools/shared/unified-share-button"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, runWhenPageVisible, stashEmailCaptureAttribution } from "@/lib/analytics"
import { cn } from "@/lib/utils"
import {
  CONFIRM_AGAINST_BAG,
  CONFIRM_AGAINST_LABEL,
  SPREADER_MODELS,
  SPREADER_PRODUCTS,
  SPREADER_PRODUCT_ALIASES,
  SPREADER_TYPE_LABEL,
} from "@/data/spreader-settings"
import {
  halfRateCrossPattern,
  lookupSpreaderSetting,
  testStripAmount,
  totalProductForArea,
} from "@/lib/spreader-settings"

const DEFAULT_SPREADER = "scotts-edgeguard-mini"
const DEFAULT_PRODUCT = "wf3"

const SELECTED_TILE = "border-2 border-green-600 bg-green-50"
const DEFAULT_TILE = "border border-gray-200 bg-white hover:border-green-300"

function parseSpreaderParam(value: string | null): string {
  return value && SPREADER_MODELS.some((m) => m.id === value) ? value : DEFAULT_SPREADER
}

function parseProductParam(value: string | null): string {
  if (!value) return DEFAULT_PRODUCT
  const resolved = SPREADER_PRODUCT_ALIASES[value] ?? value
  return SPREADER_PRODUCTS.some((p) => p.id === resolved) ? resolved : DEFAULT_PRODUCT
}

function parseSizeParam(value: string | null): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.min(n, 500000) : 0
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-[12.5px] font-semibold text-gray-700">
      {children}
    </span>
  )
}

function SpreaderSettingsContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("spreader-settings")
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()

  const [spreaderId, setSpreaderId] = useState(() => parseSpreaderParam(searchParams.get("spreader")))
  const [productId, setProductId] = useState(() => parseProductParam(searchParams.get("product")))
  const [areaSqFt, setAreaSqFt] = useState(() => parseSizeParam(searchParams.get("size")))
  const trackedUsageRef = useRef(false)

  const result = useMemo(
    () => lookupSpreaderSetting({ spreaderId, productId, areaSqFt }),
    [spreaderId, productId, areaSqFt],
  )

  // Resolves the kind from the ids being tracked, not from the render's
  // memoized result: the handlers call this right after setState, when `result`
  // still describes the previous pair.
  const trackOnce = (inputs: { spreader_id: string; product_id: string; size: number }) => {
    if (trackedUsageRef.current) return
    const next = lookupSpreaderSetting({
      spreaderId: inputs.spreader_id,
      productId: inputs.product_id,
      areaSqFt: inputs.size,
    })
    if (!next) return
    trackedUsageRef.current = true
    trackUsage("spreader-settings")
    analytics.calculatorUsed("spreader-settings", {
      inputs: { ...inputs, restored: false },
      result: { result_kind: next.kind },
      source: "live_update",
    })
  }

  // Share-link restore: the three params fully determine the answer, so a
  // recipient sees a finished result they never looked up and trackOnce (which
  // only fires on a selection change) would never run. Held until the tab is
  // visible so a background prefetch does not manufacture a phantom session.
  const restoreTrackedRef = useRef(false)
  useEffect(() => {
    const hasShareParams = Boolean(
      searchParams.get("spreader") || searchParams.get("product") || searchParams.get("size"),
    )
    if (!hasShareParams || restoreTrackedRef.current) return
    return runWhenPageVisible(() => {
      if (restoreTrackedRef.current) return
      restoreTrackedRef.current = true
      analytics.calculatorUsed("spreader-settings", {
        inputs: { spreader_id: spreaderId, product_id: productId, size: areaSqFt, restored: true },
        result: { result_kind: result?.kind ?? "none" },
        source: "url_params",
      })
    })
    // Restore state is read once from the URL the page mounted with; later
    // selection changes belong to the interaction path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const selectSpreader = (id: string) => {
    setSpreaderId(id)
    trackOnce({ spreader_id: id, product_id: productId, size: areaSqFt })
  }
  const selectProduct = (id: string) => {
    setProductId(id)
    trackOnce({ spreader_id: spreaderId, product_id: id, size: areaSqFt })
  }

  const shareUrl = `/tools/spreader-settings?spreader=${spreaderId}&product=${productId}${
    areaSqFt ? `&size=${areaSqFt}` : ""
  }`

  if (!result) return null

  const { spreader, product } = result
  // Narrow once: 'liquid' and 'prohibited' carry no rate, every other branch
  // does, so the UI reads one nullable object instead of re-testing the
  // discriminant per field.
  const rate =
    result.kind === "liquid" || result.kind === "prohibited"
      ? null
      : {
          per1000: result.targetPer1000,
          unit: result.rateUnit,
          amountLabel: result.targetAmountLabel,
          label: result.targetLabel,
        }

  const total = rate ? totalProductForArea(rate.per1000, areaSqFt) : null
  const strip = rate ? testStripAmount(rate.per1000) : null
  const half = rate ? halfRateCrossPattern(rate.per1000) : null

  // The liquid branch shows no rate at all, so a "target rate" title and the
  // rate-provenance block underneath it would both be describing a figure that
  // is not on the card.
  const cardTitle =
    result.kind === "verified"
      ? "Your dial setting"
      : result.kind === "liquid"
        ? "This one goes in a sprayer"
        : result.kind === "prohibited"
          ? "Not with this spreader"
          : "Your target rate"

  const heroFigure =
    result.kind === "verified"
      ? result.setting
      : result.kind === "prohibited"
        ? "Not by hand"
        : rate
          ? rate.amountLabel
          : "Liquid"
  const heroCaption =
    result.kind === "verified"
      ? `on the ${spreader.name} dial`
      : result.kind === "prohibited"
        ? "the label prohibits hand-held application for this product"
        : rate
          ? `${rate.unit} per 1,000 sq ft`
          : "no hopper, no dial"

  const shareText =
    result.kind === "verified"
      ? `${product.name} on a ${spreader.name}: setting ${result.setting}`
      : result.kind === "prohibited"
        ? `${product.name}: the label prohibits hand-held application`
        : `${product.name}: ${rate ? rate.label : "liquid, use a sprayer"}`

  return (
    <>
      <div className="mt-9 grid items-start gap-7 lg:grid-cols-2">
        {/* Selections. Spreader first: it is the thing the user is standing
            next to, and it is the input that decides which answer they get.
            On phones the answer takes the top slot instead, matching the
            fertilizer calculator's live preview: the selection tiles stack
            about three viewport heights tall, which buries the answer below
            them at a single column. */}
        <Card className="order-2 rounded-3xl border-gray-200 p-6 shadow-md sm:p-7 lg:order-1">
          <p className="mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
            1 · Your spreader
          </p>
          <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Spreader model">
            {SPREADER_MODELS.map((m) => (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={m.id === spreaderId}
                onClick={() => selectSpreader(m.id)}
                className={cn(
                  "flex min-h-16 flex-col gap-0.5 rounded-xl px-3.5 py-3 text-left transition-colors",
                  m.id === spreaderId ? SELECTED_TILE : DEFAULT_TILE,
                )}
              >
                <span className="text-sm font-bold text-gray-900">{m.name}</span>
                <span className="text-xs text-gray-500">{SPREADER_TYPE_LABEL[m.type]}</span>
              </button>
            ))}
          </div>

          <p className="mt-6 mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
            2 · Your product
          </p>
          <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Lawn product">
            {SPREADER_PRODUCTS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={p.id === productId}
                onClick={() => selectProduct(p.id)}
                className={cn(
                  "flex min-h-16 flex-col gap-0.5 rounded-xl px-3.5 py-3 text-left transition-colors",
                  p.id === productId ? SELECTED_TILE : DEFAULT_TILE,
                )}
              >
                <span className="text-sm font-bold text-gray-900">{p.name}</span>
                <span className="text-xs text-gray-500">
                  {p.form === "liquid" ? "Liquid" : p.npk ?? "Granular"}
                </span>
              </button>
            ))}
          </div>

          <label
            htmlFor="spreader-lawn-size"
            className="mt-6 mb-3 block text-[13px] font-bold uppercase tracking-wider text-green-700"
          >
            3 · Lawn size, optional
          </label>
          <div className="relative">
            <Ruler
              className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              id="spreader-lawn-size"
              type="number"
              inputMode="numeric"
              min={0}
              step={100}
              placeholder="5,000 sq ft"
              value={areaSqFt || ""}
              onChange={(e) => setAreaSqFt(parseSizeParam(e.target.value))}
              className="min-h-12 w-full rounded-xl border border-gray-200 bg-white pl-11 pr-4 text-[15px] font-semibold text-gray-900 outline-hidden focus:border-green-500 focus:ring-2 focus:ring-green-100"
            />
          </div>
        </Card>

        {/* Answer */}
        <div className="order-1 overflow-hidden rounded-3xl border border-green-100 bg-white shadow-md lg:order-2">
          <div className="flex items-center justify-between gap-3 bg-linear-to-r from-green-600 to-emerald-600 px-6 py-4 text-white">
            <span className="flex items-center gap-2.5 text-base font-bold">
              {result.kind === "liquid" ? (
                <Droplet className="h-5 w-5" aria-hidden="true" />
              ) : result.kind === "prohibited" ? (
                <Ban className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Gauge className="h-5 w-5" aria-hidden="true" />
              )}
              {cardTitle}
            </span>
            <UnifiedShareButton
              url={shareUrl}
              title="My spreader setting"
              text={shareText}
              variant="hero-overlay"
            />
          </div>

          <div className="p-6" aria-live="polite">
            {/* A prohibition is a stop sign, not a result, so it does not get
                the same green treatment as a number the user can act on. */}
            <div
              className={cn(
                "rounded-2xl border p-6 text-center",
                result.kind === "prohibited"
                  ? "border-rose-200 bg-rose-50"
                  : "border-green-200 bg-green-50",
              )}
            >
              <div
                className={cn(
                  "font-editorial font-extrabold leading-none",
                  result.kind === "prohibited"
                    ? "text-3xl text-rose-900 sm:text-4xl"
                    : "text-5xl text-green-900",
                )}
              >
                {heroFigure}
              </div>
              <div
                className={cn(
                  "mt-2 text-[13px] font-semibold",
                  result.kind === "prohibited" ? "text-rose-800" : "text-green-800",
                )}
              >
                {heroCaption}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {/* The spreader is not part of a liquid answer, so naming its
                  type next to "no hopper, no dial" only muddies the card. */}
              {result.kind !== "liquid" && (
                <Chip>{SPREADER_TYPE_LABEL[spreader.type]} spreader</Chip>
              )}
              <Chip>{product.short}</Chip>
              {/* Only the verified branch puts a dial number in the hero, so
                  everywhere else this chip would repeat the figure above it. */}
              {rate && result.kind === "verified" && <Chip>{rate.label}</Chip>}
              {rate && total && total.areaSqFt > 0 && (
                <Chip>
                  {total.totalLabel} {rate.unit} for {total.areaSqFt.toLocaleString("en-US")} sq ft
                </Chip>
              )}
            </div>

            {result.kind === "verified" && (
              <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-green-200 bg-green-50 px-4 py-3.5">
                <BadgeCheck className="mt-0.5 h-[18px] w-[18px] shrink-0 text-green-600" aria-hidden="true" />
                <p className="text-[13.5px] leading-relaxed text-green-900">
                  From the {result.citation}, checked {result.verifiedOn}.{" "}
                  {result.note ? `${result.note} ` : ""}
                  {CONFIRM_AGAINST_BAG}
                </p>
              </div>
            )}

            {result.kind === "calibrate" && (
              <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
                <p className="text-[13.5px] leading-relaxed text-amber-900">
                  We do not publish a dial number for this pairing because {result.reasonCopy} A
                  made-up number would cost you a bag or burn the lawn.
                </p>
                {rate && strip && half && (
                  <p className="mt-2.5 text-[13.5px] leading-relaxed text-amber-900">
                    Calibrate it instead: weigh out {strip.amountLabel} {rate.unit} for a 1,000 sq ft
                    strip, split it into two {half.halfLabel} {rate.unit} halves, and walk them at
                    right angles. The steps below have the rest. {CONFIRM_AGAINST_BAG}
                  </p>
                )}
              </div>
            )}

            {result.kind === "label-defer" && (
              <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
                <p className="text-[13.5px] leading-relaxed text-amber-900">
                  {result.reason} The rate is the part that does not move, so aim at{" "}
                  {result.targetLabel}. {CONFIRM_AGAINST_BAG}
                </p>
              </div>
            )}

            {result.kind === "prohibited" && (
              <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3.5">
                <Ban className="mt-0.5 h-[18px] w-[18px] shrink-0 text-rose-600" aria-hidden="true" />
                <p className="text-[13.5px] leading-relaxed text-rose-900">
                  {result.prohibition.copy} From the {result.prohibition.citation}, checked{" "}
                  {result.prohibition.verifiedOn}. {CONFIRM_AGAINST_BAG}
                </p>
              </div>
            )}

            {result.kind === "liquid" && (
              <div className="mt-5 rounded-xl border border-green-200 bg-green-50 px-4 py-3.5">
                <p className="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-green-900">
                  <Droplet className="mt-0.5 h-[18px] w-[18px] shrink-0 text-green-600" aria-hidden="true" />
                  <span>
                    {result.reason} Work out the ounces per gallon with the{" "}
                    <Link href="/tools/tank-mix-calculator" className="font-semibold underline underline-offset-2">
                      tank mix calculator
                    </Link>{" "}
                    instead. {CONFIRM_AGAINST_LABEL}
                  </span>
                </p>
              </div>
            )}

            {rate && (
              <>
                <p className="mt-5 mb-2.5 text-[13px] font-bold uppercase tracking-wider text-gray-500">
                  Where this rate comes from
                </p>
                <p className="text-[13.5px] leading-relaxed text-gray-600">{product.labelSource}</p>
              </>
            )}

            {product.calculatorSlug && (
              <Link
                href={`/tools/${product.calculatorSlug}`}
                className="mt-5 flex min-h-11 items-center justify-between gap-3 rounded-xl border border-green-200 bg-green-50 px-4 text-sm font-bold text-green-800 transition-transform hover:-translate-y-px"
              >
                <span>How much {product.name} for my lawn</span>
                <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <ResultEmailCaptureCard
          source="tool_result_spreader_settings"
          toolDisplayName="spreader settings"
          subcopy="Keep this setting handy and track your lawn over time."
          accountLead={{
            redirectTo: shareUrl,
            headline: "Send me the calibration steps",
            subcopy: "Keep the steps and this setting in a free account so next spring is a two minute job.",
            dividerLabel: "or get it by email",
            onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_spreader_settings"),
          }}
          successSubcopy="Your lawn tips are on the way. The calibration steps stay right here to revisit anytime."
          onCaptured={() => closeNewsletterModal()}
        />
      </div>

      <RelatedTools currentToolId="spreader-settings" prefillValues={{}} className="mt-10" />

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Spreader Settings Lookup"
        usageCount={usageCount}
      />
    </>
  )
}

/**
 * Local skeleton rather than the shared ToolPageLoadingFallback: that component
 * renders its own H1, breadcrumb, and full-page chrome, all of which this tool's
 * SERVER shell already owns. Dropping it in here would put a second H1 in the
 * SSR HTML.
 */
function LookupSkeleton() {
  return (
    <div className="mt-9 grid items-start gap-7 lg:grid-cols-2" aria-busy="true">
      <div className="h-96 rounded-3xl border border-gray-200 bg-white shadow-md" />
      <div className="h-96 rounded-3xl border border-green-100 bg-white shadow-md" />
    </div>
  )
}

export function SpreaderSettingsPageContent() {
  return (
    <Suspense fallback={<LookupSkeleton />}>
      <SearchParamsHydrationGate>
        <SpreaderSettingsContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
